import type { CityInfo, LoginResult, LoginUserInfo } from '@/service'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getLoginUserInfoApi, getUnionIdApi, selectHousingMiniCityApi } from '@/service'

/** 微信静默登录拿临时 code（uni.login 不弹授权，code 一次性有效） */
function getWxLoginCode() {
  return new Promise<string>((resolve, reject) => {
    uni.login({
      success: res => res.code ? resolve(res.code) : reject(new Error(res.errMsg)),
      fail: err => reject(err),
    })
  })
}

/**
 * 用户态（通用骨架）：只保留 token / 身份 / 登录判断等通用能力。
 * 业务相关的用户信息（如招募人、钱包等）请在业务重建时按需扩展或拆分到独立 store。
 */
export const useUserStore = defineStore(
  'user',
  () => {
    /** 用户资料（UserVo 整体落库：身份 identity、城市 cityCode/cityName、头像/姓名/手机号都在这里） */
    const userInfo = ref<LoginUserInfo>({})
    /** 登录态（登录接口返回的 LoginVo 整体落库，token 从这里取） */
    const loginInfo = ref<LoginResult>({ token: '' })
    /** 当前微信用户 openId（每次登录成功后刷新，http 拦截器随请求头 ownId 下发） */
    const openId = ref('')

    const isLogined = computed(() => !!loginInfo.value.token)
    /** 最近一次用户主动选择的城市（随 persist 持久化）：未登录时服务端记不住城市（返回 null），本地缓存即生效源 */
    const localCityInfo = ref<CityInfo | null>(null)
    /**
     * 当前浏览城市，三级兜底：服务端记忆（userInfo.cityCode / cityName，getLoginUserInfo 落库）
     * > 本地缓存（localCityInfo，未登录选城市写入）> 常熟。选城市统一走 selectCity。
     */
    const cityInfo = computed<CityInfo>(() => {
      const { cityCode, cityName } = userInfo.value
      if (cityCode != null && cityName)
        return { cityCode: String(cityCode), cityName }
      if (localCityInfo.value)
        return localCityInfo.value
      // 服务端与本地都无城市时兜底常熟
      return { cityCode: '320581', cityName: '常熟市' }
    })
    /** 手机号掩码展示（191****1680），无手机号时为空串 */
    const maskedPhone = computed(() => {
      const phone = userInfo.value.userPhone ?? ''
      if (phone.length < 11)
        return phone
      return `${phone.slice(0, 3)}****${phone.slice(7)}`
    })

    const setUserInfo = (val: LoginUserInfo) => {
      userInfo.value = val
    }

    /**
     * 登录数据处理唯一入口：LoginVo 整体落 loginInfo（token 等登录态），
     * 其中的用户资料 userInfo（UserVo）整体落 userInfo。
     */
    const setLoginResult = (result: LoginResult) => {
      loginInfo.value = result
      setUserInfo(result.userInfo ?? {})
    }

    /**
     * 调 getLoginUserInfo（返回与登录接口相同的 LoginVo），只取其中的 userInfo 落库
     * （含 cityCode/cityName 城市信息与 identity 身份）。
     * 选城市（selectHousingMiniCityApi）后必须重新调用本方法保持一致。
     * 未登录不请求；失败静默保留旧值（「我的」页 onShow 高频调用）。
     */
    const updateUserInfo = async () => {
      if (!isLogined.value)
        return
      try {
        const res = await getLoginUserInfoApi()
        setUserInfo(res.data?.userInfo ?? {})
      }
      catch {
        // 静默：保留上一次资料
      }
    }

    /**
     * 选城市唯一入口：上报服务端记忆 + 写本地缓存（+ 已登录时刷新用户信息）。
     * 已登录：上报失败向上抛（调用方保持弹层不切城市），成功后写本地并 updateUserInfo 刷新服务端记忆。
     * 未登录：服务端记不住城市（返回 null），上报仅保持链路一致（失败静默，http 层已统一 toast），
     * 本地缓存即生效源，立即写入不被上报结果阻塞。
     */
    const selectCity = async (city: CityInfo) => {
      if (isLogined.value) {
        await selectHousingMiniCityApi(city.cityCode)
        localCityInfo.value = city
        await updateUserInfo()
        return
      }
      selectHousingMiniCityApi(city.cityCode).catch(() => {})
      localCityInfo.value = city
    }

    /**
     * 刷新 openId：uni.login 静默拿 code 换 openId（登录成功后调用，每次强制刷新）。
     * 失败静默：openId 绑定微信账号而非登录态，保留旧值兜底即可，不阻塞登录主流程。
     */
    const refreshOpenId = async () => {
      try {
        const code = await getWxLoginCode()
        const res = await getUnionIdApi(code)
        if (res.data)
          openId.value = res.data
      }
      catch (error) {
        console.error('获取 openId 失败:', error)
      }
    }

    // openId / localCityInfo 绑定微信账号（设备）而非登录态，退出登录不清空（对齐 v1），reset 不处理
    const reset = () => {
      userInfo.value = {}
      loginInfo.value = { token: '' }
    }

    return {
      userInfo,
      loginInfo,
      openId,
      localCityInfo,
      cityInfo,
      isLogined,
      maskedPhone,
      setUserInfo,
      setLoginResult,
      updateUserInfo,
      selectCity,
      refreshOpenId,
      reset,
    }
  },
  {
    persist: true,
  },
)
